import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  CheckBox,
  FiservCardInterface,
  FiservCardStatusEnum,
  List,
  Preloader,
  Text,
} from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';

import { CardAccessListItem } from './CardAccessListItem';

interface Props {
  card?: FiservCardInterface;
  lockedPursesIds: string[];
  isCardLocked?: boolean;
  isLoading?: boolean;
  isEmployeeUnlockForbidden: boolean;
  onSetEmployeeUnlockForbidden: (value: boolean) => void;
  onCancel: () => void;
  onSetConfirmationMode?: () => void;
  onChangeStatusMutationHandler: (status: FiservCardStatusEnum, lockedPurseIds?: string[]) => void;
}

const CardAccessFormModal: React.FC<Props> = ({
  card,
  lockedPursesIds,
  isCardLocked,
  isLoading,
  isEmployeeUnlockForbidden,
  onSetEmployeeUnlockForbidden,
  onCancel,
  onSetConfirmationMode,
  onChangeStatusMutationHandler
}) => {
  const shouldUnlockCard = useMemo(() => !isCardLocked && lockedPursesIds.length === 0, [isCardLocked, lockedPursesIds]);

  const handleNextClick = useCallback(() => {
    if (shouldUnlockCard) {
      onChangeStatusMutationHandler(FiservCardStatusEnum.ACTIVE);
    } else {
      onSetConfirmationMode?.();
    }
  }, [onChangeStatusMutationHandler, onSetConfirmationMode, shouldUnlockCard]);

  useEffect(() => {
    if (shouldUnlockCard) {
      onSetEmployeeUnlockForbidden(false);
    }
  }, [shouldUnlockCard, onSetEmployeeUnlockForbidden]);

  return (
    <Box direction="column" data-testid="CardAccessFormModal">
      <Box round="moduleRound" pad="l" background="module">
        <List>
          <List.Header>
            <List.Header.Item flex={4}>
              Card
            </List.Header.Item>
            <List.Header.Item flex={1}>
              Access
            </List.Header.Item>
          </List.Header>    
        </List>
        <CardAccessListItem 
          card={card}
        />
        <Text color="textSecondary" margin={{ vertical: 'xs' }} >You can also lock selected accounts connected to this card.</Text>
        <Text color="textBody" weight="bold" margin={{ top: 'xs', bottom: 'm' }}>{`Accounts connected to card: ${card?.cardPurses?.length || 0}`}</Text>
        <List>
          <List.Header>
            <List.Header.Item flex={4}>
              Accounts
            </List.Header.Item>
            <List.Header.Item flex={1}>
              Access
            </List.Header.Item>
          </List.Header>    
        </List>
        {
          card?.cardPurses?.map((purse) => (
            <CardAccessListItem
              key={purse.id}
              purse={purse}
            />
          ))
        }
      </Box>
      <Box
        direction="row"
        fill="horizontal"
        margin={{ top: 'l' }}
        align="center"
        justify="between"
        width="medium"
        gap="xs"
      >
        {
          !shouldUnlockCard ? (
            <CheckBox
              name="primary"
              role="checkbox"
              aria-label="primary"
              checked={isEmployeeUnlockForbidden}
              onChange={({ target }) => onSetEmployeeUnlockForbidden(target.checked)}
              label={<Text style={{ fontSize: 14 }}>Prevent employee from modifying card access</Text>}
            />
          ) : <Box />
        }

        <Box direction="row" align="center" gap="xs">
          <AppButton
            buttonType="secondary"
            width="140px"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </AppButton>
          <AppButton
            disabled={isLoading}
            type="button"
            onClick={handleNextClick}
            width="140px"
          >
            {isLoading ? <Preloader color="white" /> : shouldUnlockCard ? 'Unlock' : 'Next'}
          </AppButton>
        </Box>
      </Box>
    </Box>
  );
};

export default CardAccessFormModal;
